import * as React from 'react';
import mustache from 'mustache';
import styled from 'styled-components';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarClickTypes,
} from '@narvar/nth-hook-context-analytics';
import TextLink, { TextLinkTargetType } from '@narvar/nth-bit-text-link';
import { useTheme, withThemeProvider } from '@narvar/nth-hook-theme';
import { MediumHeader, SmallHeader } from '@narvar/nth-bit-header';
import { LocationDetail } from '../../../../server/api/schema';
import useTempTranslations from '../useTempTranslations';

interface StoreDetailsProps {
  featureName?: string;
  storeLocation?: LocationDetail | null;
}

const HoursList = styled.ul<{ s?: React.CSSProperties; open: boolean }>(
  ({ s, open }) => ({
    listStyle: 'none',
    padding: 0,
    margin: '0.5rem 1rem 0 0',
    display: open ? undefined : 'none',
    ...s,
  }),
);

const HoursListItem = styled.li`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const HoursListItemDetail = styled.p`
  margin: 0;
`;

// TODO: move these into nth-hook-context-analytics,
// as upgrading to @latest causes react dep conflicts
const NTH_PACK_PICKUP_POINT = 'nth-pack-pickup-point';
const NTH_PACK_REDIRECT_TO_HOLD = 'nth-pack-redirect-to-hold';
const PICKUP_POINT_SHOW_HOURS_LINK = 'pickup_point_show_hours_link';
const PICKUP_POINT_HIDE_HOURS_LINK = 'pickup_point_hide_hours_link';
const REDIRECT_TO_HOLD_SHOW_HOURS_LINK = 'redirect_to_hold_show_hours_link';
const REDIRECT_TO_HOLD_HIDE_HOURS_LINK = 'redirect_to_hold_hide_hours_link';
const PICKUP_POINT_PHONE_LINK = 'pickup_point_phone_link';
const REDIRECT_TO_HOLD_PHONE_LINK = 'redirect_to_hold_phone_link';
const EXTERNAL_LINK = 'external_link'; // already in analytics schema

const StoreDetails: React.FunctionComponent<StoreDetailsProps> = ({
  featureName,
  storeLocation,
}) => {
  const [showStoreHours, setShowStoreHours] = React.useState(false);

  const logEvent = useAnalyticsPost();

  const {
    bodytextDefaultNone: textStyle,
    linksNoneDefault: linkDefault,
    linksNoneHover: linkHover,
    captiontextDefaultNone: captionStyle,
  } = useTheme();

  // TODO: update these values in nth-hook-translations and use that.
  // currently installing @latest results in react dependency conflicts.
  const {
    locationGetDirections,
    locationHoursOpenUntil,
    locationHoursOpenFuture,
    locationHideStoreHours,
    locationSeeStoreHours,
  } =
    useTempTranslations({
      translationKeys: [
        'locationGetDirections',
        'locationHoursOpenUntil',
        'locationHoursOpenFuture',
        'locationHideStoreHours',
        'locationSeeStoreHours',
      ],
    }) || {};

  const isRth = featureName === 'redirectToHold';

  const eventComponentName = isRth
    ? NTH_PACK_REDIRECT_TO_HOLD
    : NTH_PACK_PICKUP_POINT;
  const showHoursEventLink = isRth
    ? REDIRECT_TO_HOLD_SHOW_HOURS_LINK
    : PICKUP_POINT_SHOW_HOURS_LINK;
  const hideHoursEventLink = isRth
    ? REDIRECT_TO_HOLD_HIDE_HOURS_LINK
    : PICKUP_POINT_HIDE_HOURS_LINK;
  const componentPhoneLink = isRth
    ? REDIRECT_TO_HOLD_PHONE_LINK
    : PICKUP_POINT_PHONE_LINK;

  const {
    name,
    address,
    phone,
    hours,
    openStatus,
    openStatusTime,
    directionUrl,
  } = storeLocation || {};

  const addressLine1 =
    address &&
    `${address.street1}${address.street2 ? ', ' : ''}${address.street2 || ''}`;
  const addressLine2 =
    address &&
    `${address.city}${address.city ? ', ' : ''}${address.stateOrProvince} ${
      address.postalCode
    }`;

  let openStatusText;
  switch (openStatus) {
    case 'OPEN':
      openStatusText = mustache.render(locationHoursOpenUntil || '', {
        closeTime: openStatusTime,
      });
      break;
    case 'CLOSED':
      openStatusText = mustache.render(locationHoursOpenFuture || '', {
        openDayAndTime: openStatusTime,
      });
      break;
    default:
      openStatusText = '';
  }

  return (
    <>
      {name && <MediumHeader as="h3">{name}</MediumHeader>}
      <div style={textStyle}>
        {addressLine1 && <div>{addressLine1}</div>}
        {addressLine2 && <div>{addressLine2}</div>}
        {phone && (
          <TextLink
            href={`tel:${phone}`}
            linkStyle={{
              sDefault: {
                ...linkDefault,
                marginTop: '0.5rem',
                display: 'inline-block',
              },
              sHover: linkHover,
            }}
            onClick={(): void =>
              logEvent({
                clickCategory: EXTERNAL_LINK,
                clickLinkDestinationUrl: `tel:${phone}`,
                clickLinkLabel: phone,
                clickSubType: componentPhoneLink,
                clickType: NarvarClickTypes.OUTGOING,
                eventComponent: eventComponentName,
                eventFeature: eventComponentName,
                eventType: NarvarEventTypes.CLICK,
                feature: featureName,
                pageComponent: eventComponentName,
              })
            }
          >
            {phone}
          </TextLink>
        )}
        {hours && hours.length > 0 && (
          <>
            {openStatusText && (
              <SmallHeader as="p" containerCustomStyle={{ marginTop: '1rem' }}>
                {openStatusText}
              </SmallHeader>
            )}
            <div style={{ marginTop: '1rem' }}>
              <TextLink
                linkStyle={{ sDefault: linkDefault, sHover: linkHover }}
                onClick={(): void => {
                  setShowStoreHours(!showStoreHours);
                  const clickDescription = showStoreHours
                    ? hideHoursEventLink
                    : showHoursEventLink;
                  logEvent({
                    clickCategory: clickDescription,
                    clickLinkLabel: showStoreHours
                      ? locationHideStoreHours
                      : locationSeeStoreHours,
                    clickSubType: clickDescription,
                    clickType: NarvarClickTypes.INTERACTION,
                    eventComponent: eventComponentName,
                    eventFeature: eventComponentName,
                    eventType: NarvarEventTypes.CLICK,
                    feature: featureName,
                    pageComponent: eventComponentName,
                  });
                }}
                aria-expanded={showStoreHours}
                aria-controls="pickup-point-hours-list"
              >
                {showStoreHours
                  ? locationHideStoreHours
                  : locationSeeStoreHours}
              </TextLink>
            </div>
            <div style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>
              <HoursList
                id="pickup-point-store-hours-list"
                s={captionStyle}
                open={showStoreHours}
              >
                {hours?.map((hour) =>
                  hour.hoursRange ? (
                    <HoursListItem key={hour.dayOfWeek}>
                      <HoursListItemDetail>
                        {hour.dayOfWeek}
                      </HoursListItemDetail>
                      <HoursListItemDetail>
                        {hour.hoursRange}
                      </HoursListItemDetail>
                    </HoursListItem>
                  ) : (
                    <HoursListItem key={hour.dayOfWeek}>
                      <HoursListItemDetail>
                        {hour.dayOfWeek}
                      </HoursListItemDetail>
                      <HoursListItemDetail>CLOSED</HoursListItemDetail>
                    </HoursListItem>
                  ),
                )}
              </HoursList>
            </div>
          </>
        )}
        {directionUrl && (
          <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
            <TextLink
              linkStyle={{
                sDefault: linkDefault,
                sHover: linkHover,
              }}
              href={directionUrl}
              target={TextLinkTargetType.BLANK}
            >
              {locationGetDirections}
            </TextLink>
          </div>
        )}
      </div>
    </>
  );
};

export default withThemeProvider(StoreDetails, {
  themeTokens: [
    'bodytextDefaultNone',
    'linksNoneDefault',
    'linksNoneHover',
    'captiontextDefaultNone',
  ],
});
